import React from 'react';
import { Brain, Users, Trophy } from 'lucide-react';

const About = () => {
  const features = [
    {
      icon: Brain,
      title: "Hands-on AI Learning",
      description: "Learn by building real AI projects with industry-standard tools and frameworks like TensorFlow, PyTorch, and modern LLMs."
    },
    {
      icon: Users,
      title: "Student-Friendly",
      description: "Designed specifically for students with flexible schedules, peer collaboration, and beginner-friendly explanations."
    },
    {
      icon: Trophy,
      title: "Portfolio Projects",
      description: "Build impressive AI projects that showcase your skills to future employers and enhance your academic profile."
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-900 relative">
      {/* Background elements */}
      <div className="absolute inset-0 bg-circuit-pattern opacity-5"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white futuristic-text mb-6">
            About InSkill
          </h2>
          <div className="h-1 w-24 bg-gradient-to-r from-transparent via-orange-500 to-transparent mx-auto mb-8 glow-line"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            InSkill is revolutionizing AI education for students. Our intensive bootcamp combines theoretical knowledge 
            with practical experience, ensuring you're ready for the AI-driven future.
          </p>
        </div>

        {/* Mission Statement */}
        <div className="mb-16 text-center">
          <div className="bg-black/50 backdrop-blur-sm border border-orange-500/20 rounded-2xl p-8 max-w-4xl mx-auto glow-card">
            <h3 className="text-2xl font-bold text-orange-500 mb-4 futuristic-text">Our Mission</h3>
            <p className="text-gray-300 text-lg leading-relaxed">
              To empower the next generation of students with cutting-edge AI skills through hands-on learning, 
              collaborative projects, and mentorship from industry experts. We believe every student deserves 
              access to future-ready skills that will define tomorrow's workforce.
            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="bg-black/50 backdrop-blur-sm p-8 rounded-2xl border border-orange-500/20 hover:border-orange-500/40 transition-all duration-300 group glow-card-hover"
            >
              <div className="flex items-center justify-center w-16 h-16 bg-orange-500/10 rounded-full mb-6 group-hover:bg-orange-500/20 transition-colors duration-300">
                <feature.icon className="w-8 h-8 text-orange-500" />
              </div>
              
              <h3 className="text-xl font-bold text-white mb-4 futuristic-text">
                {feature.title}
              </h3>
              
              <p className="text-gray-400 leading-relaxed">
                {feature.description}
              </p>

              {/* Glowing bottom border on hover */}
              <div className="mt-6 h-1 w-0 bg-gradient-to-r from-orange-500 to-transparent group-hover:w-full transition-all duration-500"></div>
            </div>
          ))}
        </div>

        {/* Why Choose InSkill */}
        <div className="mt-20 text-center">
          <h3 className="text-3xl font-bold text-white mb-8 futuristic-text">Why Choose InSkill?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              { number: "01", title: "Expert Mentors", desc: "Learn from AI professionals" },
              { number: "02", title: "Practical Focus", desc: "Build real-world projects" },
              { number: "03", title: "Student Community", desc: "Connect with like-minded peers" },
              { number: "04", title: "Career Ready", desc: "Graduate with job-ready skills" }
            ].map((item, index) => (
              <div key={index} className="text-center space-y-3">
                <div className="text-3xl font-bold text-orange-500 futuristic-text">{item.number}</div>
                <h4 className="text-lg font-semibold text-white">{item.title}</h4>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;